import React from 'react';
import { Link } from 'react-router-dom';
import { COMPANY, addressOneLine } from '../data/company';

const Terms = () => {
  return (
    <div className="pt-24 pb-20 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 legal-content">
      <h1>Terms of Service</h1>
      <p className="text-sm border-l-2 border-accent-red pl-4 mb-8">
        Last Updated: September 2026
      </p>

      <p>
        Please read these Terms of Service carefully before using the applications, games and
        browser extensions published by {COMPANY.tradingName}.
      </p>

      <h2>1. Who we are</h2>
      <p>
        {COMPANY.tradingName} is a trading name of <strong>{COMPANY.legalName}</strong>, a company
        registered in {COMPANY.jurisdiction} under company number <strong>{COMPANY.number}</strong>.
        Our registered office is {addressOneLine}. In these Terms, "we", "us" and "our" mean
        {' '}{COMPANY.legalName}.
      </p>

      <h2>2. Acceptance of Terms</h2>
      <p>
        By downloading, installing or using any of our products — currently Chrono Arena, Eldritch
        Descent, PressVerse, TVVerse, RadioVerse and the PromptWick browser extension — you agree to
        be bound by these Terms. If you disagree with any part of them, you may not use our products.
      </p>

      <h2>3. App Functionality and Content</h2>
      <p>
        <strong>TVVerse:</strong> TVVerse is strictly a personal media player designed to parse and
        play M3U playlists. We do not host, distribute or provide any media content, playlists or
        streams.
      </p>
      <p>
        <strong>PromptWick:</strong> PromptWick sends the prompt you choose to improve to our servers,
        which pass it to a third-party language-model provider and return the rewritten text. Output
        is generated automatically and we make no warranty as to its accuracy or fitness for a
        particular purpose. You remain responsible for reviewing anything you use.
      </p>
      <p>
        <strong>User Responsibility:</strong> You are solely responsible for the content you choose to
        load, access, play or submit using our products. You must ensure you have the legal right to
        access any streams or playlists you use.
      </p>

      <h2>4. Intellectual Property</h2>
      <p>
        The products, including their original code, design, user interface and branding, are the
        exclusive property of {COMPANY.legalName}. You may not reverse engineer, decompile or extract
        the source code of the products.
      </p>
      <p>
        You keep ownership of the prompts and text you submit to PromptWick. We claim no rights over
        them beyond what is needed to provide the service to you.
      </p>

      <h2>5. Prohibited Uses</h2>
      <p>You agree not to use our products:</p>
      <ul>
        <li>For any unlawful purpose.</li>
        <li>To infringe upon the intellectual property rights of others.</li>
        <li>To distribute malware or malicious code.</li>
        <li>To attempt to circumvent usage limits, quotas or licence checks.</li>
      </ul>

      <h2>6. Purchases, subscriptions and cancellation</h2>
      <p>
        <strong>Store purchases.</strong> Our Windows applications and games are sold through the
        Microsoft Store, and PromptWick is listed in the Chrome Web Store. Purchases made through a
        store are subject to that store's own payment and refund policy, and refund requests for them
        must go to the store.
      </p>
      <p>
        <strong>PromptWick plans.</strong> Subscriptions bought directly from us are processed by
        Stripe and billed in pounds sterling plus VAT where applicable. A monthly plan renews
        automatically until cancelled. You can cancel at any time from the billing portal linked in
        the extension's side panel; cancellation stops the next renewal and your plan stays active
        until the end of the period you have paid for.
      </p>
      <p>
        <strong>Your statutory cancellation right.</strong> If you are a consumer in the UK or EU, you
        normally have 14 days to cancel a distance contract. Where you ask us to start the service
        immediately and acknowledge that you lose the right to cancel once it has been fully
        performed, that right ends accordingly. Nothing in these Terms affects your statutory rights
        under the Consumer Rights Act 2015 or the Consumer Contracts Regulations 2013.
      </p>

      <h2>7. Limitation of Liability</h2>
      <p>
        In no event shall {COMPANY.legalName} be liable for any indirect, incidental, special,
        consequential or punitive damages, including without limitation, loss of profits, data, use,
        goodwill, or other intangible losses, resulting from:
      </p>
      <ul>
        <li>Your access to or use of or inability to access or use the products.</li>
        <li>Any conduct or content of any third party accessed through the products.</li>
        <li>The content you choose to play, load or submit using the products.</li>
      </ul>
      <p>
        Nothing in these Terms excludes or limits our liability for death or personal injury caused by
        negligence, for fraud, or for any liability that cannot lawfully be excluded.
      </p>

      <h2>8. Disclaimer of Warranties</h2>
      <p>
        Our products are provided "AS IS" and "AS AVAILABLE" without warranties of any kind, whether
        express or implied, except as required by law.
      </p>

      <h2>9. Governing Law</h2>
      <p>
        These Terms are governed by and construed in accordance with the laws of
        {' '}{COMPANY.jurisdiction}, and the courts of {COMPANY.jurisdiction} have exclusive
        jurisdiction. If you are a consumer resident elsewhere in the UK or in the EU, you keep the
        benefit of any mandatory protections of the law of your country of residence.
      </p>

      <h2>10. Contact Us</h2>
      <p>
        If you have any questions about these Terms, please contact us at{' '}
        <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>, or write to us at the registered
        office above. See also our <Link to="/privacy">Privacy Policy</Link>.
      </p>
    </div>
  );
};

export default Terms;
